import * as React from 'react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TableContainer from '@mui/material/TableContainer';

import { useBoolean } from 'src/hooks/use-boolean';

import Scrollbar from 'src/components/scrollbar';
import { enqueueSnackbar } from 'src/components/snackbar';
import { useSettingsContext } from 'src/components/settings';
import {
  useTable,
  emptyRows,
  TableNoData, TableEmptyRows, TableHeadCustom,
} from 'src/components/table';

import { Client } from '../../../../types/newTypes/client';
import { languages } from '../../../../assets/data/languages';
import { ScoredProvider } from '../../../../types/newTypes/provider';
import { LoadingScreen } from '../../../../components/loading-screen';
import MatchingByClientTableRow from './matching-by-client-table-row';
import { lgbtqiaIdentity } from '../../../../assets/data/lgbtqia-identity';
import { raceAndEthnicity } from '../../../../assets/data/race-and-ethnicity';
import { bookingConstraints } from '../../../../assets/data/bookingConstraints';
import { genderIdentity } from '../../../../assets/data/demographics/gender-identity';
import { useDjangoApi } from '../../../../universal-components/djangoServerUtilities/djangoHandler';
import { specialtiesAndTreatmentModalities } from '../../../../assets/data/specialties-and-treatment-modalities';
import { meridianInPersonLocations, soTherapyInPersonLocations } from '../../../../assets/data/inPersonLocations';


const TABLE_HEAD = [
  { id: 'name', label: 'Provider', width: "25%" },
  { id: '', label: 'Percent Match', width: "20%" },
  { id: '', label: 'Oldest Referral Request', width: "25%" },
  { id: 'license', label: 'Caseload', width: "20%" },
  { id: '', label: 'View', width: "10%" }
];


export default function MatchingByClientTable() {
  const location = useLocation();

  // Get client ID from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const [clientIdFromUrl] = useState<string | null>(searchParams.get('clientId'));

  const djangoApi = useDjangoApi();
  const table = useTable({ defaultDense: true, defaultOrderBy: 'createdAt', defaultOrder: 'asc', defaultRowsPerPage: 10 });
  const settings = useSettingsContext();
  const [providers, setProviders] = useState<ScoredProvider[]>([]);
  const [tableLength, setTableLength] = useState(0);
  const isLoading = useBoolean(false);

  // TODO switch this over to pull locations from alloyDB once we know they've all been entered
  const masterListOfLocations = [{ label: 'Telehealth', value: 'Telehealth' }].concat(meridianInPersonLocations.concat(soTherapyInPersonLocations))

  const stateList = ['CA', 'UT'];
  const [payerList, setPayerList] = useState<{ payer_name: string, payer_id: string }[]>([]);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedPayer, setSelectedPayer] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [selectedRaces, setSelectedRaces] = useState<string[]>([]);
  const [selectedSexualIdentities, setSelectedSexualIdentities] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedDemographics, setSelectedDemographics] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);

  // is initially loading is used to disabled useEffects from running until after the initial loading of the client, their preferences, and their insurance (if they prefer to use insurance)
  const isInitiallyLoading = useBoolean(true);

  const setClientPreferences = (client: Client) => {
    setSelectedState(client.state)
    if (client.preferences?.language_preferences)
      setSelectedLanguages(client.preferences.language_preferences)
    if (client.preferences?.availability)
      setSelectedAvailability(bookingConstraints.filter((option) => client.preferences?.availability?.includes(option.value)).map((option) => option.label))
    if (client.preferences?.demographics_preferences) {
      setSelectedGenders(genderIdentity.filter((option) => client.preferences?.demographics_preferences?.includes(option.value)).map((option) => option.label))

      // Fix race/ethnicity mapping to match the Autocomplete transformation
      const raceValues = raceAndEthnicity.filter((option) => client.preferences?.demographics_preferences?.includes(option.value)).map((option) => option.value)
      setSelectedRaces(raceValues.map(value => value === "Not Listed" ? "BIPOC" : value))

      // Fix sexual identity mapping to use labels as expected by the Autocomplete
      const sexualIdentityValues = lgbtqiaIdentity.filter((option) => client.preferences?.demographics_preferences?.includes(option.value)).map((option) => option.label)
      setSelectedSexualIdentities(sexualIdentityValues.map(label => label === "Not Listed" ? "LGBTQ" : label))

      setSelectedSpecialties(specialtiesAndTreatmentModalities.filter((option) => client.preferences?.specialty_preferences?.includes(option.value)).map((option) => option.value))
    }
    // options={[{ label: 'In Vitro Fertilization Assessments', value: 'In Vitro Fertilization Assessments' }, { label: 'Medication Management', value: 'Medication Management' },].map((option) => option.value)}

    setSelectedServices(client.service_types.filter((service) => service === 'In Vitro Fertilization Assessments' || service === 'In Vitro Fertilization Assessments'))

  }

  useEffect(() => {
    // If we have a client, fetch them, set their preferences, then fetch their insurance (if they prefer to use insurance), then fetch the providers. 
    const getClient = async () => {
      const response = await djangoApi.get(`clients/${clientIdFromUrl}/`)
      if (response && response.status === 200) {
        setSelectedClient(response.data);
        setClientPreferences(response.data);
        if (response.data.prefers_to_use_insurance) {
          getPayers(response.data.state).then(() => {
            fetchClientInsurance().then(() => {
              getInitialProviders().then(() => {
                isInitiallyLoading.onFalse();
              });
            });
          });
        } else {
          getPayers(response.data.state).then(() => {
            getInitialProviders().then(() => {
              isInitiallyLoading.onFalse();
            });
          });
        }
      } else {
        enqueueSnackbar("Error fetching client", { variant: "error" });
      }
    }

    const fetchClientInsurance = async () => {
      // get the selected client's primary insurance for talk therapy
      const response = await djangoApi.get(`matching/client-insurance/primary-payer/?client_id=${clientIdFromUrl}`)
      if (response && response.status === 200) {
        setSelectedPayer(response.data.verifiable_payer_id);
      } else {
        enqueueSnackbar("Error fetching client insurance", { variant: "error" });
      }

    }

    isInitiallyLoading.onTrue();
    if (clientIdFromUrl) {
      getClient();
    } else {
      isInitiallyLoading.onFalse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // state is an optional param that can be used when react isn't fast enough to update the state of the selected state
  const getPayers = async (state?: string) => {
    isLoading.onTrue();
    const response = await djangoApi.get(`datasets/verifiable-payer-list/?state=${state || selectedState}`)
    if (response && response.status === 200) {
      setPayerList(response.data);
    } else {
      enqueueSnackbar("Error fetching payers", { variant: "error" });
    }
    isLoading.onFalse();
  }

  useEffect(() => {
    if (!isInitiallyLoading.value) {
      if (selectedState !== '')
        getPayers();
      else
        setPayerList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState]);


  // these are the initial providers that are fetched when the client is loaded using the client's preferences to filter providers. It doesn't use the filters that users see infront of them, although those will be populated.
  const getInitialProviders = async () => {
    const response = await djangoApi.post('matching/clients/score_providers/', { client_id: clientIdFromUrl, limit: 10 })
    if (response && response.status === 200) {
      setProviders(response.data.results);
      setTableLength(response.data.total_count);
    } else {
      enqueueSnackbar("Error fetching providers", { variant: "error" });
    }
  }

  // Helper function to format filters for Django backend
  const formatOverrideFilters = () => {
    const filters: Record<string, any> = {};

    if (selectedState) {
      filters.license__license_state = selectedState;
    }

    if (selectedLanguages.length > 0) {
      filters.languages_spoken__overlap = selectedLanguages;
    }

    if (selectedLocation) {
      filters.in_person_locations__contains = [selectedLocation];
    }

    if (selectedGenders.length > 0) {
      if (selectedGenders.length === 1) {
        filters.gender_identity = selectedGenders[0];
      } else {
        filters.gender_identity__in = selectedGenders;
      }
    }

    if (selectedSexualIdentities.length > 0) {
      filters.lgbtqia_identity__overlap = selectedSexualIdentities;
    }

    if (selectedRaces.length > 0) {
      filters.race_and_ethnicity__overlap = selectedRaces;
    }

    if (selectedSpecialties.length > 0) {
      filters.clinical_specialties__overlap = selectedSpecialties;
    }

    return filters;
  };

  // this fetches provider using the filters that users see infront of them.
  const getProviders = async () => {
    isLoading.onTrue();
    const response = await djangoApi.post('matching/clients/score_providers/', {
      client_id: clientIdFromUrl,
      limit: 10,
      override_filters: formatOverrideFilters(),
      merge_strategy: 'override'
    })
    if (response && response.status === 200) {
      setProviders(response.data.results);
      setTableLength(response.data.total_count);
    } else {
      enqueueSnackbar("Error fetching providers", { variant: "error" });
    }
    isLoading.onFalse();
  }


  useEffect(() => {
    if (!isInitiallyLoading.value) {
      getProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedState,
    selectedPayer,
    selectedLanguages,
    selectedLocation,
    selectedGenders,
    selectedSexualIdentities,
    selectedRaces,
    selectedSpecialties,
    selectedServices,
    selectedAvailability
  ])

  useEffect(() => {
    let demographics = selectedGenders;

    if (selectedSexualIdentities.includes("LGBTQ"))
      demographics = demographics.concat(lgbtqiaIdentity.map((identity) => identity.label))
    else
      demographics = demographics.concat(selectedSexualIdentities)

    if (selectedRaces.includes("BIPOC"))
      if (selectedRaces.includes("White"))
        demographics = demographics.concat(raceAndEthnicity.map((race) => race.label))
      else
        demographics = demographics.concat(raceAndEthnicity.filter((race) => race.label !== "White").map((race) => race.label))
    else
      demographics = demographics.concat(selectedRaces)
    setSelectedDemographics(demographics)
  }, [selectedRaces, selectedGenders, selectedSexualIdentities])


  const resetFilters = () => {
    setSelectedSpecialties([]);
    setSelectedServices([]);
    setSelectedPayer('');
    setSelectedLanguages([]);
    setSelectedLocation('');
    setSelectedRaces([]);
    setSelectedGenders([]);
    setSelectedSexualIdentities([]);
    setSelectedState('');
    setProviders([]);
    setSelectedClient(null)
    setSelectedAvailability([]);
  }


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction='row' alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h3">Filters</Typography>
          <Stack direction='row' alignItems="center" sx={{ width: 'auto' }} spacing={2}>
            {selectedClient && <Typography>Client: {selectedClient.legal_first_name} {selectedClient.legal_last_name}</Typography>}
            <Button variant="contained" size='large' sx={{ width: '90px' }} onClick={resetFilters}>Reset</Button>
          </Stack>
        </Stack>
        {isInitiallyLoading.value && 'hello'}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={stateList}
              value={selectedState}
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOption) => {
                if (selectedOption)
                  setSelectedState(selectedOption);
                else {
                  setSelectedState('');
                  setSelectedPayer('');
                }

              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={payerList.map((payer) => payer.payer_id)}
              value={selectedPayer}
              autoHighlight
              getOptionLabel={(option) => {
                const payer = payerList.find((p) => p.payer_id === option);
                return payer?.payer_name || '';
              }}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {payerList.find((payer) => payer.payer_id === option)?.payer_name}
                  </Box>
                );
              }}
              onChange={(event, selectedOption) => {
                if (selectedOption)
                  setSelectedPayer(selectedOption);
                else
                  setSelectedPayer('')

              }}

              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Payer"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              multiple
              options={languages
                .filter((option) => option.label !== 'Not Listed') // Filter out "Not Listed"
                .map((option) => option.label)}
              value={selectedLanguages}
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOption) => {
                if (selectedOption)
                  setSelectedLanguages(selectedOption);
                else
                  setSelectedLanguages([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Language"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              disabled
              options={[{ label: 'In Vitro Fertilization Assessments', value: 'In Vitro Fertilization Assessments' }, { label: 'Medication Management', value: 'Medication Management' },].map((option) => option.value)}
              value={selectedServices}
              multiple
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}

              onChange={(event, selectedOptions) => {
                setSelectedServices(selectedOptions);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Services Offered (disabled)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              disabled
              options={masterListOfLocations.map((option) => option.value)}
              value={selectedLocation}
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOption) => {
                if (selectedOption)
                  setSelectedLocation(selectedOption);
                else
                  setSelectedLocation('');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location (disabled)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={bookingConstraints.map((option) => option.label)}
              value={selectedAvailability}
              multiple
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOptions) => {
                if (selectedOptions)
                  setSelectedAvailability(selectedOptions);
                else
                  setSelectedAvailability([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Booking Constraints"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={genderIdentity
                .filter((option) => option.label !== 'Not Listed') // Filter out "Not Listed"
                .map((option) => option.label)}
              value={selectedGenders}
              multiple
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOptions) => {
                setSelectedGenders(selectedOptions);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Gender Identity"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={lgbtqiaIdentity.map((option) => option.label === "Not Listed" ? "LGBTQ" : option.label)} // just replacing the not listed option with an all encompassing LGBTQ option
              value={selectedSexualIdentities}
              multiple
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOptions) => {
                setSelectedSexualIdentities(selectedOptions);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sexual Identity"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={raceAndEthnicity.map((option) => option.value === "Not Listed" ? "BIPOC" : option.value)}
              value={selectedRaces}
              multiple
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOptions) => {
                setSelectedRaces(selectedOptions);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Race/Ethnicity"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disabled={isInitiallyLoading.value || isLoading.value}
              options={specialtiesAndTreatmentModalities
                .filter((option) => option.value !== 'Not Listed') // Filter out "Not Listed"
                .map((option) => option.value)}
              value={selectedSpecialties}
              multiple
              autoHighlight
              getOptionLabel={(option) => `${option}`}
              renderOption={(props, option) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    {option}
                  </Box>
                );
              }}
              onChange={(event, selectedOptions) => {
                setSelectedSpecialties(selectedOptions);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Specialties"
                />
              )}
            />
          </Grid>

        </Grid>
      </Card>

      {isLoading.value || isInitiallyLoading.value ?
        <LoadingScreen sx={{ height: '200px' }} />
        :
        <Card>
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={providers.length}
                />
                <TableBody>
                  {providers.map((row) => (
                    // TODO: fix the clientID param
                    <MatchingByClientTableRow
                      key={row.provider_id}
                      scoredProvider={row}
                      selectedSpecialties={selectedSpecialties}
                      selectedDemographics={selectedDemographics}
                      clientID={clientIdFromUrl || 'test'}
                      selectedState={selectedState}
                    />
                  ))}

                  <TableEmptyRows
                    height={44}
                    emptyRows={emptyRows(table.page, table.rowsPerPage, providers.length)}
                  />

                  <TableNoData notFound={providers.length === 0} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          {/* <TablePaginationCustom
            count={tableLength}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
          /> */}

        </Card>
      }
    </Container>

  );
}